var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { DexfSettingsUtility, ProductUtility, ShoppingSupport } from '@inter-ikea-kompis/utilities';
import { AddToList } from '@inter-ikea-kompis/component-add-to-list';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';
import { checkmark, registerIcon, shoppingBagAdd, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import AddToBagLayoutEnum from '../../enums/AddToBagLayoutEnum.js';
import AddToBagModalEnum from '../../enums/AddToBagModalEnum.js';
import AddToBagStateEnum from '../../enums/AddToBagStateEnum.js';
import AddToListLayoutEnum from '../../enums/AddToListLayoutEnum.js';
import UnavailableOnlineBehaviourEnum from '../../enums/UnavailableOnlineBehaviourEnum.js';
import AddToBagTracker from '../../trackers/AddToBagTracker.js';
import UnavailableProductsCard from '../unavailable-products-card/UnavailableProductsCard.js';
import AddToBagStyle from './AddToBagStyle.js';
/**
 * View component for adding products to shopping list and shopping cart.
 *
 * @event addToCart Start adding products to shopping cart.
 * @event addToList Start adding products to shopping list.
 * @event addToCartClick User clicks on add to cart button.
 * @event modalClose Modal close
 * @event modalOpen Modal open
 * @event addUnavailableProductsToListChange Changes the option "addUnavailableProductsToList". Tiggered by user clicking the checkbox
 * @event addUnavailableToCartAndList Start adding unavailable products to shopping cart and shopping list
 * @event unavailableProductsModalContinue Click on continue in the unavailable products modal.
 * @event unavailableProductsCardCancelClick User click cancel in the unavailbleProductsCard dialog.
 * @event resetAddToCartState Sent after 3 seconds after addToCartState confirmation to be able to reset the state of the button.
 * @event resetAddToListState Sent after 3 seconds after addToListState confirmation to be able to reset the state of the button.
 */
export default class AddToBag extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Defines the state for add to bag button
        this.unavailableOnlineBehaviour = UnavailableOnlineBehaviourEnum.blockPurchase;
        // Layout
        this.layout = AddToBagLayoutEnum.text;
        // Defines the state for add to bag button
        this.addToCartState = AddToBagStateEnum.default;
        // Defines the state for add to list button
        this.addToListState = AddToBagStateEnum.default;
        // Disables the buttons.
        this.disabled = false;
        // Modal visible
        this.visibleModal = null;
        // Selects the checkbox for the UnavailableProductsCard that adds unavailable items when user clicks "continue". Only available if markets supports shopping lists.
        this.addUnavailableProductsToList = false;
        // List of failed shopping products returned when trying to add to cart.
        this.failedShoppingItems = null;
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        // Hides the add to list button.
        this.hideListButton = false;
        this.promptElement = null;
        this.cardElement = null;
        this.triggerElement = null;
        this.tracker = null;
        this.addToCartTimeout = null;
        this.addToListTimeout = null;
        this.dexfSettings = null;
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${AddToBagStyle.getStyle({
            theme: this.theme
        })}
			</style>

			${this.getInlineMessage()}
			<div class="buttonContainer">${this.getButtons()}</div>
		`;
    }
    async connectedCallback() {
        registerIcon(checkmark);
        registerIcon(shoppingBagAdd);
        registerIcon(warningTriangle);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('kompis-add-to-list', AddToList);
        this.customElements.define('kompis-unavailable-products-card', UnavailableProductsCard);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closePrompt();
        this.disconnectTracker();
        if (this.addToCartTimeout) {
            clearTimeout(this.addToCartTimeout);
        }
        if (this.addToListTimeout) {
            clearTimeout(this.addToListTimeout);
        }
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        // Once the add to bag-button have been set with the state 'confirmation' there should be an event sent after 3 seconds to make it possible to reset its state to 'default' again.
        if (changedProperties.has('addToCartState') &&
            this.addToCartState === AddToBagStateEnum.confirmation) {
            this.dispatchResetAddToCartState();
        }
        // Once the add to list-button have been set with the state 'confirmation' there should be an event sent after 3 seconds to make it possible to reset its state to 'default' again.
        if (changedProperties.has('addToListState') &&
            this.addToListState === AddToBagStateEnum.confirmation) {
            this.dispatchResetAddToListState();
        }
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === AddToBagModalEnum.unavailableProductCard) {
                this.openPrompt();
            }
            else {
                this.closePrompt();
            }
        }
        if (changedProperties.has('analytics') && this.hasTranslationsAndSettings()) {
            this.disconnectTracker();
            this.connectTracker();
        }
        this.forwardPropsToCard();
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    dispatchResetAddToCartState() {
        if (this.addToCartTimeout) {
            clearTimeout(this.addToCartTimeout);
        }
        this.addToCartTimeout = setTimeout(() => {
            this.dispatchEvent(new CustomEvent('resetAddToCartState', { bubbles: true }));
        }, 3000);
    }
    dispatchResetAddToListState() {
        if (this.addToListTimeout) {
            clearTimeout(this.addToListTimeout);
        }
        this.addToListTimeout = setTimeout(() => {
            this.dispatchEvent(new CustomEvent('resetAddToListState', { bubbles: true }));
        }, 3000);
    }
    async connectTracker() {
        if (this.analytics) {
            const reporter = this.analytics.__INTERNAL__reporter;
            if (reporter?.connected === false) {
                // Should use an existing reporter, but wait for it to be connected
                return;
            }
            // Use existing connected reporter, or the tracker should create a new one.
            const reporterId = reporter ? reporter.id : undefined;
            this.tracker = new AddToBagTracker({
                ...this.analytics,
                settings: this.getSettings(),
                reporterId
            });
            await this.tracker?.connect();
        }
    }
    disconnectTracker() {
        this.tracker?.disconnect();
        this.tracker = null;
    }
    closePrompt() {
        if (this.promptElement) {
            this.promptElement.open = false;
            this.promptElement = null;
            this.cardElement = null;
        }
    }
    getButtons() {
        const isOnlineSaleable = this.isOnlineSaleable();
        if (this.layout === AddToBagLayoutEnum.text) {
            return html `${this.getCartButton({ isOnlineSaleable })} ${this.getDivider()}
			${this.getListButton()}`;
        }
        return html `
			${this.getListButton()} ${this.getDivider()} ${this.getCartButton({ isOnlineSaleable })}
		`;
    }
    getListButton() {
        if (!this.shouldShowAddToList()) {
            return null;
        }
        const layout = this.getAddToListLayout();
        return html `
			<kompis-add-to-list
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.shoppingProducts="${this.shoppingProducts}"
				.analytics="${this.getSubComponentAnalytics()}"
				layout="${layout}"
				state="${this.addToListState}"
				?disabled="${this.disabled || !this.hasPositiveQuantity()}"
				@addToList="${this.onAddToList.bind(this)}"
			>
			</kompis-add-to-list>
		`;
    }
    /**
     * Get analytics object to pass on to sub components.
     */
    getSubComponentAnalytics() {
        const reporterId = this.tracker?.getConnectedReporterId();
        return this.analytics
            ? {
                ...this.analytics,
                __INTERNAL__reporter: reporterId
                    ? { connected: true, id: reporterId }
                    : { connected: false }
            }
            : null;
    }
    getAddToListLayout() {
        if (this.layout === AddToBagLayoutEnum.icon) {
            return AddToListLayoutEnum.iconSmall;
        }
        if (ShoppingSupport.hasCartSupport(this.getSettings())) {
            return AddToListLayoutEnum.icon;
        }
        return AddToListLayoutEnum.text;
    }
    getCartButton(options) {
        if (!ShoppingSupport.hasCartSupport(this.getSettings())) {
            return null;
        }
        const { loading, addToShoppingCartButtonSuccess, addToShoppingBagButton } = this.getTranslations();
        const disabled = this.disabled || !options.isOnlineSaleable || !this.hasPositiveQuantity();
        const isLoading = !disabled && this.addToCartState === AddToBagStateEnum.loading;
        const success = this.addToCartState === AddToBagStateEnum.confirmation;
        const label = success ? addToShoppingCartButtonSuccess : addToShoppingBagButton;
        const emphasisedVariant = 'emphasised';
        const icon = !disabled && this.addToCartState === AddToBagStateEnum.confirmation ? 'checkmark' : null;
        if (this.layout === AddToBagLayoutEnum.icon) {
            // Potential accessibility issue? Buttons should always have some sort of label or description
            return html `
				<skapa-icon-button
					?loading="${isLoading}"
					loadingSrText="${loading}"
					size="small"
					variant="${emphasisedVariant}"
					?disabled="${disabled}"
					@click="${this.onAddToCart.bind(this)}"
				>
					${label}
					<skapa-icon slot="icon" icon="${icon || 'shopping-bag-add'}"></skapa-icon>
				</skapa-icon-button>
			`;
        }
        return html `
			<skapa-button
				variant="${emphasisedVariant}"
				?loading="${isLoading}"
				loadingSrText="${loading}"
				?disabled="${disabled}"
				fluid
				@click="${this.onAddToCart.bind(this)}"
			>
				${icon ? html `<skapa-icon slot="icon" icon="${icon}"></skapa-icon>` : null} ${label}
			</skapa-button>
		`;
    }
    shouldShowAddToList() {
        return ShoppingSupport.hasListSupport(this.getSettings()) && !this.hideListButton;
    }
    getDivider() {
        if (!ShoppingSupport.hasCartSupport(this.getSettings()) || !this.shouldShowAddToList()) {
            return null;
        }
        return html ` <div class="divider"></div> `;
    }
    setTriggerElement(target) {
        if (target instanceof Element) {
            this.triggerElement = this.getTriggerElement();
        }
        return this.triggerElement;
    }
    getTriggerElement() {
        return ((this.shadowRoot
            .querySelector(this.customElements.getInternalName('skapa-button'))
            ?.shadowRoot?.querySelector('button') ||
            this.shadowRoot
                .querySelector(this.customElements.getInternalName('skapa-icon-button'))
                ?.shadowRoot?.querySelector('button')));
    }
    onAddToCart(event) {
        const triggerElement = this.setTriggerElement(event.currentTarget);
        const shoppingItems = ProductUtility.getShoppingItems(this.getValidShoppingProducts());
        this.dispatchEvent(new CustomEvent('addToCartClick', {
            bubbles: true,
            detail: {
                shoppingItems,
                triggerElement
            }
        }));
        this.tracker?.sendAddToCartClick();
        if (this.includesUnavailableProducts()) {
            this.dispatchEvent(new CustomEvent('modalOpen', {
                bubbles: true,
                detail: {
                    visibleModal: AddToBagModalEnum.unavailableProductCard,
                    shoppingItems,
                    triggerElement
                }
            }));
            if (ShoppingSupport.hasListSupport(this.getSettings())) {
                this.dispatchEvent(new CustomEvent('addUnavailableProductsToListChange', {
                    bubbles: true,
                    detail: {
                        addUnavailableProductsToList: true
                    }
                }));
            }
        }
        else {
            this.dispatchEvent(new CustomEvent('addToCart', {
                bubbles: true,
                detail: {
                    shoppingItems,
                    completeConfiguration: true
                }
            }));
        }
    }
    onAddToList(event) {
        this.dispatchEvent(new CustomEvent('addToList', {
            bubbles: true,
            detail: {
                ...event.detail
            }
        }));
        this.triggerElement = event.detail.triggerElement;
    }
    getInlineMessage() {
        return html `<div aria-live="assertive">${this.getCartUnavailableDisclaimer()}</div>`;
    }
    getCartUnavailableDisclaimer() {
        if (this.isOnlineSaleable() ||
            !ShoppingSupport.hasCartSupport(this.getSettings()) ||
            (this.unavailableOnlineBehaviour === UnavailableOnlineBehaviourEnum.blockPurchase &&
                this.layout === AddToBagLayoutEnum.icon)) {
            return null;
        }
        const { addToShoppingCartUnavailable, addToShoppingCartUnavailableShort } = this.getTranslations();
        const text = this.layout === AddToBagLayoutEnum.text
            ? addToShoppingCartUnavailable
            : addToShoppingCartUnavailableShort;
        const cautionaryVariant = 'cautionary';
        return html `
			<div class="shoppingCartUnavailableMessage">
				<skapa-inline-message subtle class="message" variant="${cautionaryVariant}">
					<skapa-icon slot="message-icon" icon="warning-triangle"></skapa-icon>
					<span slot="body">${text}</span>
				</skapa-inline-message>
			</div>
		`;
    }
    getValidShoppingProducts() {
        return this.shoppingProducts
            .filter((shoppingProduct) => shoppingProduct.quantity > 0)
            .filter((shoppingProduct) => shoppingProduct.product.content);
    }
    getUnavailableShoppingProducts() {
        return this.shoppingProducts
            .filter((shoppingProduct) => shoppingProduct.quantity > 0)
            .filter((shoppingProduct) => !this.isShoppingProductOnlineSaleable(shoppingProduct));
    }
    isShoppingProductOnlineSaleable(shoppingProduct) {
        const disableOnlineSaleableVerification = this.getSettings().kompis.localization.disableOnlineSaleableVerification;
        return Boolean(shoppingProduct.product.content?.priceInformation?.availableForHomeDelivery ||
            shoppingProduct.product.content?.priceInformation?.availableForClickCollect ||
            disableOnlineSaleableVerification);
    }
    isOnlineSaleable() {
        const disableOnlineSaleableVerification = this.getSettings().kompis.localization.disableOnlineSaleableVerification;
        if (disableOnlineSaleableVerification ||
            this.unavailableOnlineBehaviour === UnavailableOnlineBehaviourEnum.continueByShoppingList) {
            return true;
        }
        return !this.includesUnavailableProducts();
    }
    includesUnavailableProducts() {
        return this.shoppingProducts
            .filter((shoppingProduct) => shoppingProduct.quantity > 0)
            .some((shoppingProduct) => !this.isShoppingProductOnlineSaleable(shoppingProduct));
    }
    hasPositiveQuantity() {
        return this.shoppingProducts.some((shoppingProduct) => shoppingProduct.quantity > 0);
    }
    openPrompt() {
        if (this.promptElement) {
            return;
        }
        const promptElement = this.createPromptElement();
        this.promptElement = promptElement;
        document.body.appendChild(promptElement);
        promptElement.open = true;
    }
    forwardPropsToCard() {
        if (this.cardElement) {
            this.cardElement.theme = this.theme;
            this.cardElement.shoppingProducts = this.shoppingProducts;
            this.cardElement.addToShoppingList = this.addUnavailableProductsToList;
            this.cardElement.localizedInformation = this.localizedInformation;
            this.cardElement.settings = this.settings;
            this.cardElement.translations = this.translations;
            this.cardElement.failedShoppingItems = this.failedShoppingItems;
        }
    }
    createPromptElement() {
        const promptElement = this.createPrompt();
        const heading = this.createPromptHeading();
        const cardElement = this.createCard();
        const promptFooterElement = this.createPromptFooter();
        promptElement.appendChild(heading);
        promptElement.appendChild(cardElement);
        promptElement.appendChild(promptFooterElement);
        return promptElement;
    }
    createPrompt() {
        const promptElement = this.createInternalElement('skapa-prompt');
        promptElement.setAttribute('aria-label', this.getTranslations().addToShoppingCartUnavailableDialogueHeader);
        promptElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        promptElement.style.zIndex = '500';
        promptElement.addEventListener('closerequest', () => {
            if (this.promptElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        });
        promptElement.addEventListener('closed', () => {
            promptElement.parentElement?.removeChild(promptElement);
        });
        return promptElement;
    }
    createPromptHeading() {
        const heading = document.createElement('h2');
        heading.slot = 'title';
        heading.innerText = this.getTranslations().addToShoppingCartUnavailableDialogueHeader;
        heading.className = Text.classnames({ heading: { m: true } });
        heading.style.margin = '0';
        const style = document.createElement('style');
        style.textContent = Text.CSS;
        heading.appendChild(style);
        return heading;
    }
    createCard() {
        this.cardElement = this.createInternalElement('kompis-unavailable-products-card');
        this.forwardPropsToCard();
        this.cardElement.addEventListener('addToShoppingList', (event) => {
            this.dispatchEvent(new CustomEvent('addUnavailableProductsToListChange', {
                bubbles: true,
                detail: {
                    addUnavailableProductsToList: event.detail.addToShoppingList
                }
            }));
        });
        return this.cardElement;
    }
    getShoppingItems() {
        const failedShoppingItemNos = this.failedShoppingItems?.map(({ itemNo }) => itemNo) ?? [];
        const shoppingItems = ProductUtility.getShoppingItems(this.getValidShoppingProducts()
            .filter(this.isShoppingProductOnlineSaleable.bind(this))
            .filter((sp) => !failedShoppingItemNos.includes(sp.product.content?.ruItemNo ?? '')));
        const failedShoppingProducts = this.shoppingProducts.filter((sp) => failedShoppingItemNos.includes(sp.product.content?.ruItemNo ?? ''));
        const shoppingListItems = ProductUtility.getShoppingItems([
            ...this.getUnavailableShoppingProducts(),
            ...failedShoppingProducts
        ]);
        return { shoppingListItems, shoppingCartItems: shoppingItems };
    }
    createPromptFooter() {
        const { buttonCancel, buttonContinue } = this.getTranslations();
        const promptFooterElement = this.createInternalElement('skapa-modal-footer');
        promptFooterElement.slot = 'footer';
        promptFooterElement.innerText = buttonContinue;
        const secondaryButtonSpan = document.createElement('span');
        secondaryButtonSpan.slot = 'secondary';
        secondaryButtonSpan.innerText = buttonCancel;
        promptFooterElement.appendChild(secondaryButtonSpan);
        promptFooterElement.addEventListener('primary', () => {
            this.dispatchEvent(new CustomEvent('modalClose', {
                bubbles: true,
                detail: {
                    visibleModal: null,
                    triggerElement: this.triggerElement
                }
            }));
            const { shoppingCartItems, shoppingListItems } = this.getShoppingItems();
            this.dispatchEvent(new CustomEvent('unavailableProductsModalContinue', {
                bubbles: true,
                detail: {
                    shoppingItems: shoppingCartItems
                }
            }));
            this.tracker?.sendHandleUnavailableItems(shoppingListItems, this.addUnavailableProductsToList, 'continue');
            if (this.addUnavailableProductsToList) {
                const detail = {
                    shoppingCartItems,
                    shoppingListItems
                };
                this.dispatchEvent(new CustomEvent('addUnavailableToCartAndList', { bubbles: true, detail }));
            }
            else {
                this.dispatchEvent(new CustomEvent('addToCart', {
                    bubbles: true,
                    detail: {
                        shoppingItems: shoppingCartItems,
                        triggerElement: this.getTriggerElement()
                    }
                }));
            }
        });
        promptFooterElement.addEventListener('secondary', () => {
            this.dispatchEvent(new CustomEvent('modalClose', {
                bubbles: true,
                detail: {
                    visibleModal: null,
                    triggerElement: this.triggerElement
                }
            }));
            this.dispatchEvent(new CustomEvent('unavailableProductsCardCancelClick', {
                bubbles: true
            }));
            const { shoppingListItems } = this.getShoppingItems();
            this.tracker?.sendHandleUnavailableItems(shoppingListItems, this.addUnavailableProductsToList, 'cancel');
        });
        return promptFooterElement;
    }
}
__decorate([
    property({ type: Object, required: true })
], AddToBag.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], AddToBag.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], AddToBag.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], AddToBag.prototype, "settings", void 0);
__decorate([
    property({ type: String })
], AddToBag.prototype, "unavailableOnlineBehaviour", void 0);
__decorate([
    property({ type: String })
], AddToBag.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], AddToBag.prototype, "addToCartState", void 0);
__decorate([
    property({ type: String })
], AddToBag.prototype, "addToListState", void 0);
__decorate([
    property({ type: Object, required: true })
], AddToBag.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: Boolean })
], AddToBag.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], AddToBag.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Boolean })
], AddToBag.prototype, "addUnavailableProductsToList", void 0);
__decorate([
    property({ type: Object })
], AddToBag.prototype, "failedShoppingItems", void 0);
__decorate([
    property({ type: Object })
], AddToBag.prototype, "analytics", void 0);
__decorate([
    property({ type: Boolean })
], AddToBag.prototype, "hideListButton", void 0);
//# sourceMappingURL=AddToBag.js.map